// WebAuthn encoding helpers — browser-native only, no external dependencies.

/** Convert a Base64url string to a Uint8Array (for use as a WebAuthn buffer). */
export function b64urlToUint8Array(b64url: string): Uint8Array {
  // Replace URL-safe chars and add padding
  const base64 = b64url.replace(/-/g, '+').replace(/_/g, '/');
  const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), '=');
  const binary = atob(padded);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/** Convert an ArrayBuffer or Uint8Array to a Base64url string. */
export function uint8ArrayToB64url(buf: ArrayBuffer | Uint8Array): string {
  const bytes = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

/**
 * Serialize a PublicKeyCredential assertion response for transmission to the
 * verify endpoint.
 */
export function serializeAssertionResponse(cred: PublicKeyCredential): object {
  const response = cred.response as AuthenticatorAssertionResponse;
  return {
    id: cred.id,
    rawId: uint8ArrayToB64url(cred.rawId),
    type: cred.type,
    response: {
      clientDataJSON: uint8ArrayToB64url(response.clientDataJSON),
      authenticatorData: uint8ArrayToB64url(response.authenticatorData),
      signature: uint8ArrayToB64url(response.signature),
      userHandle:
        response.userHandle != null ? uint8ArrayToB64url(response.userHandle) : null,
    },
  };
}

/**
 * Serialize a PublicKeyCredential *registration* (attestation) response for
 * transmission to the register-verify endpoint. Shape matches
 * RegistrationResponseJSON (a la @simplewebauthn/browser).
 */
export function serializeRegistrationResponse(cred: PublicKeyCredential): object {
  const response = cred.response as AuthenticatorAttestationResponse;

  const responseJson: Record<string, unknown> = {
    clientDataJSON: uint8ArrayToB64url(response.clientDataJSON),
    attestationObject: uint8ArrayToB64url(response.attestationObject),
  };

  // getTransports() is widely supported but not universal — guard for
  // browsers/authenticators that lack it.
  if (typeof response.getTransports === 'function') {
    try {
      responseJson['transports'] = response.getTransports();
    } catch {
      // ignore — omit transports if the call throws
    }
  }

  const out: Record<string, unknown> = {
    id: cred.id,
    rawId: uint8ArrayToB64url(cred.rawId),
    type: cred.type,
    response: responseJson,
    clientExtensionResults: cred.getClientExtensionResults(),
  };

  const attachment = (cred as PublicKeyCredential & { authenticatorAttachment?: string | null })
    .authenticatorAttachment;
  if (attachment != null) {
    out['authenticatorAttachment'] = attachment;
  }

  return out;
}

// Minimal shape of PublicKeyCredentialCreationOptionsJSON as returned by the
// register-options endpoint (base64url-encoded buffers).
export interface WebAuthnCreationExcludeCredential {
  id: string;
  type: string;
  transports?: AuthenticatorTransport[];
}

export interface WebAuthnCreationOptionsJSON {
  rp: { id?: string; name: string };
  user: { id: string; name: string; displayName: string };
  challenge: string;
  pubKeyCredParams: PublicKeyCredentialParameters[];
  timeout?: number;
  excludeCredentials?: WebAuthnCreationExcludeCredential[];
  authenticatorSelection?: AuthenticatorSelectionCriteria;
  attestation?: AttestationConveyancePreference;
}

/**
 * Decode a PublicKeyCredentialCreationOptionsJSON (base64url challenge /
 * user.id / excludeCredentials[].id) to the ArrayBuffer form required by
 * navigator.credentials.create().
 */
export function decodeCreationOptions(
  opts: WebAuthnCreationOptionsJSON,
): PublicKeyCredentialCreationOptions {
  const excludeCredentials: PublicKeyCredentialDescriptor[] | undefined =
    opts.excludeCredentials?.map((c) => {
      const desc: PublicKeyCredentialDescriptor = {
        id: b64urlToUint8Array(c.id).buffer as ArrayBuffer,
        type: c.type as PublicKeyCredentialType,
      };
      if (c.transports !== undefined) {
        desc.transports = c.transports;
      }
      return desc;
    });

  return {
    rp: opts.rp,
    user: {
      id: b64urlToUint8Array(opts.user.id).buffer as ArrayBuffer,
      name: opts.user.name,
      displayName: opts.user.displayName,
    },
    challenge: b64urlToUint8Array(opts.challenge).buffer as ArrayBuffer,
    pubKeyCredParams: opts.pubKeyCredParams,
    ...(opts.timeout !== undefined && { timeout: opts.timeout }),
    ...(excludeCredentials !== undefined && { excludeCredentials }),
    ...(opts.authenticatorSelection !== undefined && {
      authenticatorSelection: opts.authenticatorSelection,
    }),
    ...(opts.attestation !== undefined && { attestation: opts.attestation }),
  };
}
